import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TooltipItem, ChartOptions } from 'chart.js';
import { Push } from 'connected-react-router';
import { Bar } from 'react-chartjs-2';
import { trackEvent } from 'utils/analytics';
import { SpotInterface } from 'types';
import { useRevenueContext } from 'contexts/RevenueContext';
import { HTTPError } from 'clients/HTTPClient';
import { UrlUpdaterFunctions } from 'hooks/useFilters';
import { compactCurrency, convertCentsToDisplayPrice, currencyWithoutCents } from 'utils/currency';
import { monthForSelect } from 'utils/monthYear';
import { scrollMainToTop } from 'utils/helpers';
import LinesIcon from 'assets/icons/lines.svg?react';
import InfoIcon from 'components/Icons/InfoIcon';
import Loader from 'components/Loader/Loader';
import WidgetBox from 'components/widgets/WidgetBox';
import AGMessage from 'components/AGMessage/AGMessage';
import Label from 'components/AGForm/Inputs/Label/Label';
import AGSimpleSelect from 'components/AGSelect/AGSimpleSelect/AGSimpleSelect';
import { AuthorizedView, Roles } from 'components/Utilities/AuthorizedView';
import ArrowRightIcon from 'components/Icons/ArrowRightIcon';
import Button from 'components/Button/Button';
import ReloadIcon from 'components/Icons/ReloadIcon';
import InfoBox from './InfoBox';
import {
    ChartId,
    filteredLegendOptions,
    getYoYRevenueGraph,
    PRIMARY_COLOR,
    RevenueType,
    revenueTypeOptions,
} from './helpers';
import styles from './RevenueWidgets.module.css';

type YoYRevenueWidgetProps = {
    spot: SpotInterface;
    push: Push;
    filters: { yoyRevenueType: RevenueType; month: string };
    urlUpdater: UrlUpdaterFunctions<{
        yoyRevenueType: RevenueType;
        month: string;
    }>;
};

const now = new Date();

function YoYRevenueWidget({ spot, push: connectedPush, filters, urlUpdater }: YoYRevenueWidgetProps): JSX.Element {
    const { getYoyRevenueReport, yoyRevenueReport: report } = useRevenueContext();
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [graphData, setGraphData] = useState<
        {
            labels: string[];
            suggestedMax: number;
        } & { [key in RevenueType]: { airGarage: (number | null)[]; historical: (number | null)[] } }
    >({
        [RevenueType.GROSS_REVENUE]: { airGarage: [], historical: [] },
        [RevenueType.NET_REVENUE]: { airGarage: [], historical: [] },
        labels: [],
        suggestedMax: 0,
    });

    const isCurrentMonth = useMemo(() => Number(filters.month) === now.getMonth() + 1, [filters.month]);

    const options: ChartOptions<'bar'> = useMemo(() => {
        return {
            scales: {
                x: { stacked: true },
                y: {
                    grid: { display: true, borderDash: [2] },
                    ticks: {
                        callback(tickValue) {
                            return typeof tickValue === 'number' ? compactCurrency.format(tickValue) : '';
                        },
                        maxTicksLimit: 4,
                    },
                    suggestedMax: graphData.suggestedMax,
                },
            },
            plugins: {
                tooltip: {
                    callbacks: {
                        label(tooltipItem: TooltipItem<'bar'>): string | string[] {
                            return typeof tooltipItem.raw === 'number'
                                ? currencyWithoutCents.format(tooltipItem.raw)
                                : tooltipItem.formattedValue;
                        },
                    },
                },
                datalabels: {
                    display: true,
                    anchor: 'end',
                    align: 'top',
                    formatter: (value) => {
                        return typeof value === 'number' ? `${compactCurrency.format(value)}` : '';
                    },
                    font: { weight: 'bold' },
                    color: 'black',
                },
                legend: filteredLegendOptions,
            },
        };
    }, [graphData.suggestedMax]);

    const getRevenueData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await getYoyRevenueReport(spot.uuid, filters.month);
            setGraphData(getYoYRevenueGraph(response));
        } catch (e) {
            if (HTTPError.isHTTPError(e)) {
                setErrorMessage(e.message);
            } else {
                setErrorMessage(e?.toString() || 'Something went wrong');
            }
        } finally {
            setLoading(false);
        }
    }, [spot.uuid, filters.month]);

    useEffect(() => {
        getRevenueData();
    }, [spot.uuid, filters.month]);

    function onRevenueTypeChange(selection: string) {
        urlUpdater.yoyRevenueType(selection);
        trackEvent(`Select YoY ${selection}`, { spot: spot.pk, spotName: spot.name });
    }

    function onUploadFinancialsClick() {
        trackEvent('Clicked upload financials from YoY widget', { spot: spot.pk, spotName: spot.name });
        connectedPush(`/organization/${spot.organization}/documents?spotPk=${spot.pk}`);
        scrollMainToTop();
    }

    function onMonthChange(selection: string) {
        urlUpdater.month(selection);
        trackEvent('Change YoY month', { spot: spot.pk, spotName: spot.name, month: selection });
    }

    return (
        <WidgetBox title="Historical Comparisons">
            <>
                <div className={styles.filters}>
                    <div>
                        <Label label="Metric" />
                        <AGSimpleSelect
                            selected={filters.yoyRevenueType}
                            onSelect={onRevenueTypeChange}
                            options={revenueTypeOptions}
                            icon={<LinesIcon />}
                            width="170px"
                        />
                    </div>
                    <div>
                        <Label label="Month" />
                        <AGSimpleSelect
                            selected={filters.month}
                            onSelect={onMonthChange}
                            options={monthForSelect}
                            icon={<LinesIcon />}
                            maxHeight="280px"
                            width="170px"
                        />
                    </div>
                </div>

                {errorMessage && <AGMessage color="error">{errorMessage}</AGMessage>}
                {loading || !report ? (
                    <Loader centered style={{ marginTop: '20px' }} />
                ) : report.reports.length > 1 ? (
                    <div>
                        <div className={styles.infoBoxRow}>
                            <InfoBox
                                header="Year-over-Year Change"
                                data={report.uplift.yoy_gross_revenue}
                                percentage={report.uplift.yoy_gross_revenue_percent}
                                percentageInfo={report.uplift.yoy_gross_revenue_date}
                            />
                            <InfoBox
                                header="Monthly Average with AirGarage"
                                data={report.uplift.current_average}
                                percentage={report.uplift.growth_percent}
                                percentageInfo={`${convertCentsToDisplayPrice(
                                    report.uplift.prior_average
                                )} Average Before`}
                            />
                        </div>
                        <div id={ChartId.HISTORICAL_CHART} style={{ paddingBlock: '32px' }}>
                            <Bar
                                data={{
                                    labels: graphData.labels,
                                    datasets: [
                                        {
                                            data: graphData[filters.yoyRevenueType].historical,
                                            backgroundColor: '#50ACC6',
                                            label: 'Before AirGarage',
                                        },
                                        {
                                            data: graphData[filters.yoyRevenueType].airGarage,
                                            backgroundColor: PRIMARY_COLOR,
                                            label: 'AirGarage',
                                        },
                                    ],
                                }}
                                options={options}
                            />
                        </div>
                    </div>
                ) : (
                    !isCurrentMonth && (
                        <div className={styles.emptyChartBox} style={{ marginTop: '20px' }}>
                            <ReloadIcon />
                            <h3>No historical financials provided</h3>
                            <p>Want to see how AirGarage stacks up against your prior operator?</p>
                            <AuthorizedView
                                allowed={Roles.Admin}
                                fallback={
                                    <p>
                                        Reach out to your Partner Success Manager to upload your historical revenue and
                                        compare performance over time.
                                    </p>
                                }
                            >
                                <Button color="info" onClick={onUploadFinancialsClick} size="sm" fitContent>
                                    Upload your financials now <ArrowRightIcon />
                                </Button>
                            </AuthorizedView>
                        </div>
                    )
                )}
                {isCurrentMonth && (
                    <div className={styles.currentMonth}>
                        <InfoIcon color="#9196A1" width={20} height={20} />
                        <p>
                            We can’t compare {now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })} yet.
                            Comparisons are only made when the month is fully complete.
                        </p>
                    </div>
                )}
            </>
        </WidgetBox>
    );
}

export default YoYRevenueWidget;
